import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const AuthContext = createContext(null);

function getStoredToken() {
  return localStorage.getItem('vq_access_token') || sessionStorage.getItem('vq_access_token');
}

function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(getStoredToken);

  const user = useMemo(() => {
    const claims = decodeJwtPayload(token);
    if (!claims) return null;
    return { userId: claims.sub, tenantId: claims.tenantId, role: claims.role, plan: claims.plan || 'SOLO', emailVerified: claims.emailVerified === true };
  }, [token]);

  const login = useCallback((accessToken, refreshToken, remember = false) => {
    // Overwrite-then-clear-other (instead of clearing every key first and
    // then setting): keeps the same-key storage event in sibling tabs as
    // a single non-null update, so cross-tab listeners don't see a brief
    // null and accidentally log themselves out.
    const store = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    store.setItem('vq_access_token', accessToken);
    store.setItem('vq_refresh_token', refreshToken);
    other.removeItem('vq_access_token');
    other.removeItem('vq_refresh_token');
    setToken(accessToken);
  }, []);

  const logout = useCallback(() => {
    const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;
    const currentToken = localStorage.getItem('vq_access_token') || sessionStorage.getItem('vq_access_token');
    if (currentToken) {
      fetch(`${API_BASE}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    localStorage.removeItem('vq_access_token');
    localStorage.removeItem('vq_refresh_token');
    sessionStorage.removeItem('vq_access_token');
    sessionStorage.removeItem('vq_refresh_token');
    setToken(null);
  }, []);

  // Cross-tab sync: when another tab refreshes the access token (e.g. the
  // verify-email page swaps in a JWT with emailVerified=true) or logs out,
  // pick up the new value here so this tab's banner / role-gated UI
  // reflects the latest claims without requiring a manual reload.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'vq_access_token') return;
      // newValue null = sibling logged out; non-null = sibling refreshed.
      setToken(e.newValue || null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
